import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Briefcase, Calendar, Clock, MapPin, User, Play, CheckCircle2, 
  AlertTriangle, ChevronRight, Filter, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const MyJobs = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, today, all
  const [startingJob, setStartingJob] = useState(null);

  useEffect(() => {
    fetchMyJobs();
  }, [filter]);

  const fetchMyJobs = async () => {
    setLoading(true);
    try {
      // Get jobs assigned to the current user (owner)
      const params = {};
      
      if (filter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.date_from = today;
        params.date_to = today;
      }
      
      const response = await api.get('/jobs', { params });
      
      // Filter jobs assigned to the owner
      let filteredJobs = response.data.filter(job => job.worker_id === user?.id);
      
      // Apply status filter
      if (filter === 'pending') {
        filteredJobs = filteredJobs.filter(job => 
          job.status === 'scheduled' || job.status === 'in_progress'
        );
      }
      
      // Sort by date and emergency status
      filteredJobs.sort((a, b) => {
        if (a.is_emergency && !b.is_emergency) return -1;
        if (!a.is_emergency && b.is_emergency) return 1;
        return new Date(a.scheduled_date) - new Date(b.scheduled_date);
      });
      
      setJobs(filteredJobs);
    } catch (error) {
      toast.error('Error al cargar trabajos');
    } finally {
      setLoading(false);
    }
  };

  const startJob = async (job) => {
    setStartingJob(job.id);
    try {
      // Update job status to in_progress
      await api.put(`/jobs/${job.id}`, { status: 'in_progress' });
      
      // Navigate to work report page with job data
      navigate(`/complete-job/${job.id}`);
    } catch (error) {
      toast.error('Error al iniciar el trabajo');
      setStartingJob(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      scheduled: { class: 'bg-blue-100 text-blue-800', label: 'Programado' },
      in_progress: { class: 'bg-yellow-100 text-yellow-800', label: 'En progreso' },
      completed: { class: 'bg-green-100 text-green-800', label: 'Completado' },
      cancelled: { class: 'bg-gray-100 text-gray-800', label: 'Cancelado' }
    };
    const { class: className, label } = config[status] || config.scheduled;
    return <Badge className={className}>{label}</Badge>;
  };

  const getJobTypeBadge = (type) => {
    const config = {
      mantenimiento: { class: 'bg-sky-100 text-sky-800', label: 'Mantenimiento' },
      reparacion: { class: 'bg-orange-100 text-orange-800', label: 'Reparación' },
      revision: { class: 'bg-purple-100 text-purple-800', label: 'Revisión' },
      urgencia: { class: 'bg-red-100 text-red-800', label: 'Urgencia' },
      instalacion: { class: 'bg-emerald-100 text-emerald-800', label: 'Instalación' }
    };
    const { class: className, label } = config[type] || { class: 'bg-gray-100 text-gray-800', label: type };
    return <Badge variant="outline" className={className}>{label}</Badge>;
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="my-jobs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Briefcase className="w-7 h-7 text-sky-600" />
              Mis Trabajos
            </h1>
            <p className="text-slate-500">Trabajos asignados a ti para completar</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="today">Hoy</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchMyJobs}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {jobs.filter(j => j.status === 'scheduled').length}
                  </p>
                  <p className="text-sm text-slate-500">Programados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {jobs.filter(j => j.status === 'in_progress').length}
                  </p>
                  <p className="text-sm text-slate-500">En progreso</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {jobs.filter(j => j.is_emergency && j.status !== 'completed').length}
                  </p>
                  <p className="text-sm text-slate-500">Urgentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {jobs.filter(j => j.status === 'completed').length}
                  </p>
                  <p className="text-sm text-slate-500">Completados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Jobs List */}
        <Card>
          <CardHeader>
            <CardTitle>Lista de Trabajos</CardTitle>
            <CardDescription>
              {jobs.length === 0 
                ? 'No tienes trabajos asignados' 
                : `${jobs.length} trabajo${jobs.length !== 1 ? 's' : ''} encontrado${jobs.length !== 1 ? 's' : ''}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {jobs.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Sin trabajos asignados</h3>
                <p className="text-slate-500 mb-4">
                  Cuando te asignes trabajos desde el calendario, aparecerán aquí
                </p>
                <Button onClick={() => navigate('/calendar')}>
                  Ir al Calendario
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {jobs.map((job, index) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-4 rounded-lg border transition-all ${
                      job.is_emergency 
                        ? 'bg-red-50 border-red-200 hover:border-red-300' 
                        : 'bg-slate-50 border-transparent hover:border-slate-200'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      {/* Job Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h4 className="font-semibold text-slate-900">{job.title}</h4>
                          {job.is_emergency && (
                            <Badge className="bg-red-500 text-white animate-pulse">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              URGENTE
                            </Badge>
                          )}
                          {getStatusBadge(job.status)}
                          {getJobTypeBadge(job.job_type)}
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="truncate">{job.client_name || 'Cliente'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            <span className="truncate">{job.client_address || 'Sin dirección'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span>{format(new Date(job.scheduled_date), "EEEE d 'de' MMMM", { locale: es })}</span>
                          </div>
                          {job.scheduled_time && (
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span>{job.scheduled_time}</span>
                            </div>
                          )}
                        </div>
                        
                        {job.description && (
                          <p className="mt-2 text-sm text-slate-500 line-clamp-2">{job.description}</p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex sm:flex-col gap-2 sm:w-auto">
                        {job.status === 'scheduled' && (
                          <Button 
                            onClick={() => startJob(job)}
                            disabled={startingJob === job.id}
                            className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700"
                          >
                            {startingJob === job.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-2" />
                                Iniciar
                              </>
                            )}
                          </Button>
                        )}
                        {job.status === 'in_progress' && (
                          <Button 
                            onClick={() => navigate(`/complete-job/${job.id}`)}
                            className="flex-1 sm:flex-none"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Completar
                          </Button>
                        )}
                        {job.status === 'completed' && (
                          <Button 
                            variant="outline"
                            onClick={() => navigate(`/reports`)}
                            className="flex-1 sm:flex-none"
                          >
                            Ver Parte
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </OwnerLayout>
  );
};

export default MyJobs;
